/* eslint-disable no-unused-vars */
import React, { useEffect } from 'react';
import {
  object, string, number, array,
} from 'yup';
import {
  omit, startCase, isEqual, get, cloneDeep, pick,
} from 'lodash';
import { useHistory } from 'react-router-dom';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import moment from 'moment';
import { faSync } from '@fortawesome/free-solid-svg-icons/faSync';

import { env } from '~/commons/config';
import { objMap, rolesCheck } from '~/commons/helpers';
import useHttpStatus from '~/commons/hooks/useHttpStatus';

import Alert from '~/commons/components/Alert.jsx';
import FormGroup from '~/commons/components/FormGroup.jsx';
import FormCheckGroup from '~/commons/components/FormCheckGroup.jsx';
import ListTable from '~/commons/components/ListTable.jsx';
import FieldArrayCards from './FieldArrayCards.jsx';
import FormResource from './FormResource.jsx';
import { countryObj } from './orders/commons';
import { FormConvertBtn } from './orders';
import { FieldErpStatus, erpStatusHelper } from './FieldErpStatus.jsx';

const schema = object({
  name: object({
    full: object({
      en: string().required().label('English Name'),
      zhHant: string(),
    }),
  }),
  orgId: rolesCheck('admin') ? number().required() : number(),
  email: string().email(),
  mobile: object({
    code: string().matches(/^\+?\d{1,3}$/, 'Please enter a valid country code.'),
    number: string().matches(/^[\d-]+$/, 'Please enter a valid phone number.'),
  }),
  contacts: array().of(object({
    firstname: string(),
  })),
  resaleCerts: array().of(object({
    cert: string(),
    startAt: string(),
    endAt: string(),
    state: string(),
  })),
});

const arrDefaults = {
  contacts: {
    active: true,
    erpId: '',
    firstname: '',
    middlename: '',
    lastname: '',
    email: '',
    mobile: '',
    phone: '',
    fax: '',
    title: '',
    remarks: '',
  },
  extra: {
    walved: false,
    serviceType: '',
    service: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    status: '',
    deliServ: '',
    deliTrackNo: '',
    amount: 0,
    remarks: '',
  },
  extraItems: {
    description: '',
    price: 0,
  },
  addresses: {
    active: true,
    billing: false,
    shipping: false,
    primaryBilling: false,
    primaryShipping: false,

    erpId: '',
    address1: '',
    address2: '',
    address3: '',
    address4: '',
    city: '',
    state: '',
    province: '',
    county: '',
    country: '',
    postCode: '',
    countryCode: '',
    remarks: '',
  },
};

const defaults = {
  active: true,
  roles: ['customer'],
  email: '',
  name: { full: { en: '', zhHant: '' } },
  mobile: {
    number: '',
    code: '',
  },
  addresses: [arrDefaults.addresses],
  remarks: '',
  referredBy: '',
  wishList: '',
  tfyWishList: '',
  restockWishList: '',
  contacts: [arrDefaults.contacts],
  source: '',
  erpId: '',
  acctNo: '',
  orgId: '',
  acctCategory: '',
  marketingOptoutEmail: false,
  acceptTerms: true,
  resaleCerts: [],
  erpSync: {
    manualSync: false,
    lastSuccess: '',
    lastSync: '',
    errType: '',
    message: '',
  },
};

const FormCustomer = ({ customer, services }) => {
  const history = useHistory();
  const api = '/api/core/users/customers';
  const [res, req] = useHttpStatus();
  const [infoWindow, setInfoWindow] = React.useState(false);
  useEffect(() => {
    if (res.payload) {
      setInfoWindow(true);
    }
  }, [res]);

  return (
    <FormResource
      {...{
        defaults: { ...defaults, source: customer ? '' : 'Created in CMS' },
        schema,
        stored: customer
          ? {
            acceptTerms: false,
            ...customer,
            erpSync: erpStatusHelper(customer.erpSync),
            browseHistory: customer.browseHistory?.map((v) => ({
              ...v,
              browseAt: moment(v.browseAt).format('lll'),
            })),
            resaleCerts: customer.resaleCerts?.map((v) => ({
              ...v,
              startAt: v.startAt ? moment(v.startAt).format('YYYY-MM-DD') : '',
              endAt: v.endAt ? moment(v.endAt).format('YYYY-MM-DD') : '',
            })),
          }
          : undefined,
        api,
        onSubmit: (v) => {
          const data = cloneDeep(v);

          ['addresses', 'contacts'].forEach((x) => {
            const output = data[x].filter((y) => !isEqual(y, arrDefaults[x]));
            data[x] = output.length > 0 ? output : [];
          });

          data.addresses = data.addresses.map((x) => ({
            ...x,
            country: countryObj[x.countryCode],
          }));
          data.resaleCerts = data.resaleCerts?.map((x) => ({
            ...x,
            startAt: x.startAt ? moment(x.startAt).startOf('day').toDate() : null,
            endAt: x.endAt ? moment(x.endAt).endOf('day').toDate() : null,
          }));

          data.orgId = parseInt(data.orgId, 10) || undefined;
          data.erpSync = pick(data.erpSync, ['manualSync']);

          return {
            ...omit(data, ['wishList', 'tfyWishList', 'restockWishList', 'shoppingCart', 'questionnaireAns', 'browseHistory']),
          };
        },
      }}>
      <>
        {customer && (
          <FormConvertBtn
            res={res}
            icon={faSync}
            onClick={() => {
              req({
                url: `${api}/manualSync`,
                method: 'POST',
                data: {
                  _id: customer._id,
                },
              });
            }}>
            Sync
          </FormConvertBtn>
        )}
        {infoWindow && (
          <Alert theme="info" onClose={() => setInfoWindow(false)}>
            {JSON.stringify(res.payload)}
          </Alert>
        )}
        <Tabs defaultActiveKey="customer">
          <Tab eventKey="customer" title="Customer / Company">
            <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">BASIC</h6>
            <div className="row">
              <div className="col">
                <div className="form-group">
                  <label>Active</label>
                  <FormCheckGroup name="active" type="checkbox" label="" asSwitch />
                </div>
              </div>
              <div className="col">
                <div className="form-group">
                  <label>Marketing Optout Email</label>
                  <FormCheckGroup name="marketingOptoutEmail" type="checkbox" label="" asSwitch />
                </div>
              </div>
              <div className="col">
                <div className="form-group">
                  <label>Accept T&C</label>
                  <FormCheckGroup name="acceptTerms" type="checkbox" label="" asSwitch />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col">
                <FormGroup name="erpId" disabled />
              </div>
              <div className="col">
                <FormGroup name="acctCategory" />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <FormGroup name="name.full.en" label="Name (English)" />
              </div>
              <div className="col">
                <FormGroup name="name.full.zhHant" label="Name (Chinese)" />
              </div>
            </div>
            <div className="row">
              <div className="col-6">
                <FormGroup name="email" />
              </div>
              <div className="col-2">
                <FormGroup name="mobile.code" label="Country Code" />
              </div>
              <div className="col-4">
                <FormGroup name="mobile.number" />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <FormGroup name="referredBy" />
              </div>
              <div className="col">
                <FormGroup
                  name="orgId"
                  label="Organization ID"
                  disabled={get(env, 'user.roles', []).includes('department')}
                />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <FormGroup name="source" disabled />
              </div>
            </div>
            <FormGroup name="remarks" label="Customer Remarks" />
          </Tab>
          <Tab eventKey="addresses" title="Addresses">
            <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">ADDRESS</h6>
            <div className="row">
              <div className="col">
                <FieldArrayCards name="addresses" push={arrDefaults.addresses}>
                  {(s, i) => (
                    <>
                      <div className="row">
                        {['active', 'billing', 'shipping', 'primaryBilling', 'primaryShipping'].map(
                          (v, x) => (
                            <div className="col" key={x}>
                              <div className="form-group">
                                <label>{startCase(v)}</label>
                                <FormCheckGroup
                                  name={`addresses[${i}].${v}`}
                                  type="checkbox"
                                  label=""
                                  asSwitch
                                />
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormGroup name={`addresses[${i}].address1`} label="Address 1" />
                        </div>
                        <div className="col">
                          <FormGroup name={`addresses[${i}].address2`} label="Address 2" />
                        </div>
                        <div className="col">
                          <FormGroup name={`addresses[${i}].address3`} label="Address 3" />
                        </div>
                        <div className="col">
                          <FormGroup name={`addresses[${i}].address4`} label="Address 4" />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormGroup name={`addresses[${i}].city`} label="City" />
                        </div>
                        <div className="col">
                          <FormGroup name={`addresses[${i}].state`} label="State" as="select">
                            <option value=""> - </option>
                            {env.stateCode.map((x) => (
                              <option value={x.code} key={x.code}>
                                {x.code} - {x.name}
                              </option>
                            ))}
                          </FormGroup>
                        </div>
                        {get(s, 'countryCode') === 'US' ? (
                          <div className="col">
                            <FormGroup name={`addresses[${i}.county`} label="County" />
                          </div>
                        ) : (
                          <div className="col">
                            <FormGroup name={`addresses[${i}.province`} label="Province" />
                          </div>
                        )}
                        <div className="col">
                          <FormGroup
                            name={`addresses[${i}].countryCode`}
                            label="Country"
                            value={countryObj[get(s, 'countryCode')] || ''}
                            onChange={() => {}}
                            disabled
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormGroup name={`addresses[${i}].postCode`} label="Post Code" />
                        </div>
                        <div className="col">
                          <FormGroup
                            name={`addresses[${i}].countryCode`}
                            label="Country Code"
                            as="select">
                            <option value=""> - </option>
                            {env.countryCode.map((x) => (
                              <option value={x.code} key={x.code}>
                                {x.code} - {x.short}
                              </option>
                            ))}
                          </FormGroup>
                        </div>
                        <div className="col">
                          <FormGroup name={`addresses[${i}].remarks`} label="Remark" />
                        </div>
                        <div className="col">
                          <FormGroup name={`addresses[${i}].erpId`} label="ERP ID" disabled />
                        </div>
                      </div>
                    </>
                  )}
                </FieldArrayCards>
              </div>
            </div>
          </Tab>
          <Tab eventKey="contacts" title="Contacts">
            <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">CONTACTS</h6>
            <div className="row">
              <div className="col">
                <FieldArrayCards name="contacts" push={arrDefaults.contacts}>
                  {(s, i) => (
                    <>
                      <div className="row">
                        <div className="col">
                          <div className="form-group">
                            <label>Active</label>
                            <FormCheckGroup
                              name={`contacts[${i}].active`}
                              type="checkbox"
                              label=""
                              asSwitch
                            />
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormGroup name={`contacts[${i}].erpId`} label="Erp ID" disabled />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormGroup name={`contacts[${i}].firstname`} label="First Name" />
                        </div>
                        <div className="col">
                          <FormGroup name={`contacts[${i}].middlename`} label="Middle Name" />
                        </div>
                        <div className="col">
                          <FormGroup name={`contacts[${i}].lastname`} label="Last Name" />
                        </div>
                        <div className="col">
                          <FormGroup name={`contacts[${i}].title`} label="Title" />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormGroup name={`contacts[${i}].phone`} label="Phone" />
                        </div>
                        <div className="col">
                          <FormGroup name={`contacts[${i}].mobile`} label="Mobile" />
                        </div>
                        <div className="col">
                          <FormGroup name={`contacts[${i}].fax`} label="Fax" />
                        </div>
                        <div className="col">
                          <FormGroup name={`contacts[${i}].email`} label="Email" />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormGroup name={`contacts[${i}].remarks`} label="Remarks" />
                        </div>
                      </div>
                    </>
                  )}
                </FieldArrayCards>
              </div>
            </div>
          </Tab>

          <Tab eventKey="resale" title="Resale Certs">
            <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">Resale Certs</h6>
            <div className="row">
              <div className="col">
                <FieldArrayCards name="resaleCerts" disabled>
                  {(s, i) => (
                    <>
                      <div className="row">
                        <div className="col">
                          <FormGroup name={`resaleCerts[${i}].cert`} label="Cert Number" disabled />
                        </div>
                        <div className="col">
                          <FormGroup name={`resaleCerts[${i}].state`} label="State" disabled />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormGroup
                            name={`resaleCerts[${i}].startAt`}
                            type="date"
                            label="Start At"
                            disabled
                          />
                        </div>
                        <div className="col">
                          <FormGroup
                            name={`resaleCerts[${i}].endAt`}
                            type="date"
                            label="End At"
                            disabled
                          />
                        </div>
                      </div>
                    </>
                  )}
                </FieldArrayCards>
              </div>
            </div>
          </Tab>
          <Tab eventKey="browseHistory" title="Browse">
            <h6 className="small text-muted mb-1 pt-4">BROWSE HISTORY</h6>
            {(() => {
              const rows = customer ? customer.browseHistory : [];
              return (
                <ListTable
                  {...{
                    className: '',
                    cols: [
                      { key: 'design.design', children: 'Design' },
                      { key: 'design.brand', children: 'Brand' },
                      { key: 'design.coll', children: 'Coll' },
                      {
                        key: 'browseAt',
                        getter: (v) => moment(v).format(env.f.dt),
                      },
                    ],
                    rows,
                    onRowClick: (row) => {
                      history.push(`/admin/designs/items/${row.design?.id || ''}`);
                    },
                  }}
                />
              );
            })()}
          </Tab>
          <Tab eventKey="erpStats" title="ERP Status">
            <FieldErpStatus />
          </Tab>
        </Tabs>
      </>
    </FormResource>
  );
};

export default FormCustomer;
